import { useEffect, useState } from "react";
import { Button, Modal } from "@monolith-forensics/monolith-ui";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { useDeleteCaseFile } from "../files.query";
import { File } from "../files.types";
import { useCaseFiles } from "../Hooks/useCaseFiles";
import { EvidenceFileModalAlert } from "@/Features/EvidenceFiles/EvidenceFileModalAlert";

export const DeleteCaseFileModal: React.FC<{
  show: boolean;
  onClose?: () => void;
  onSubmit?: (data: File[]) => void;
  onCancel?: () => void;
  defaultFormData: File;
  buttonProps?: {
    submitText?: string;
    cancelText?: string;
  };
}> = ({
  show,
  onClose,
  onSubmit,
  onCancel,
  buttonProps = {},
  defaultFormData,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const { api } = useCaseFiles();
  const { mutate: deleteCaseFile } = useDeleteCaseFile({ api });

  const isFolder = defaultFormData.is_folder;

  const handleSubmit = async () => {
    setSubmitting(true);

    deleteCaseFile(
      {
        uuid: defaultFormData.uuid,
      },
      {
        onSuccess: (data) => {
          setSubmitting(false);
          // httpDelete resolves { success: false, error } on HTTP failures
          // instead of throwing, so a failed delete lands here, not in onError.
          if (data?.success === false) return;
          onSubmit?.(data.data);
        },
        onError: (error) => {
          setSubmitting(false);
          console.error(error);
        },
      }
    );
  };

  const handleCancel = () => {
    if (submitting) return;
    (onCancel || onClose)?.();
  };

  // The base Modal does not dismiss on escape, so handle it here.
  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleCancel();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, submitting, onCancel, onClose]);

  return (
    <Modal open={show} onClose={handleCancel} showCloseButton={true}>
      <Modal.Title>Delete {isFolder ? "Folder" : "File"}</Modal.Title>
      <EvidenceFileModalAlert file={defaultFormData} kind="delete" />
      <div style={{ fontWeight: 600, margin: "20px 0px", width: "350px" }}>
        Are you sure you want to delete this {isFolder ? "folder" : "file"}?
      </div>
      <div style={{ fontWeight: 600, marginBottom: "20px", width: "350px" }}>
        "{defaultFormData.name}"
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          {buttonProps?.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="error"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          {buttonProps?.submitText || "Delete"}
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
